import React from "react";

export interface ElementDimensions<T extends HTMLElement> {
    ref: React.Ref<T>;
    dimensions: DimensionModel;
}

export interface DimensionModel {
    width: number;
    height: number;
    innerWidth: number;
    innerHeight: number;

    minWidth(minWidth: number): boolean;
    maxWidth(maxWidth: number): boolean;
}

const makeDimension = ({
    width,
    height,
    innerWidth,
    innerHeight,
}: {
    width: number;
    height: number;
    innerWidth: number;
    innerHeight: number;
}): DimensionModel => ({
    width,
    height,
    innerWidth,
    innerHeight,
    minWidth: (min) => width >= min,
    maxWidth: (max) => width < max,
});

export function useElementDimensions<T extends HTMLElement>(
    initialElement?: T,
): ElementDimensions<T> {
    const [dimensions, setDimensions] = React.useState(
        makeDimension({ width: 0, height: 0, innerWidth: 0, innerHeight: 0 }),
    );
    const [element, setElement] = React.useState(initialElement);
    const ref: React.Ref<T> = (instance) => setElement(instance);

    React.useLayoutEffect(() => {
        function calcDimensions() {
            window.requestAnimationFrame(() => {
                if (element) {
                    const width = element.offsetWidth;
                    const height = element.offsetHeight;

                    const cs = window.getComputedStyle(element);
                    const hpadding = parseInt(cs.paddingLeft, 10) + parseInt(cs.paddingRight, 10);
                    const vpadding = parseInt(cs.paddingTop, 10) + parseInt(cs.paddingBottom, 10);
                    const innerWidth = element.offsetWidth - hpadding;
                    const innerHeight = element.offsetHeight - vpadding;

                    setDimensions(makeDimension({ width, height, innerWidth, innerHeight }));
                }
            });
        }

        const observer = new MutationObserver((mutations) => {
            if (
                mutations.some(
                    (mutation) =>
                        mutation.type === "attributes" && mutation.attributeName === "style",
                )
            ) {
                calcDimensions();
            }
        });

        window.addEventListener("resize", calcDimensions);
        if (window.visualViewport) {
            window.visualViewport.addEventListener("resize", calcDimensions);
        }
        window.addEventListener("orientationchange", calcDimensions);
        if (element) {
            observer.observe(element, { attributes: true });
        }
        calcDimensions();

        return () => {
            window.removeEventListener("resize", calcDimensions);
            if (window.visualViewport) {
                window.visualViewport.removeEventListener("resize", calcDimensions);
            }
            window.removeEventListener("orientationchange", calcDimensions);
            observer.disconnect();
        };
    }, [element]);

    return { ref, dimensions };
}
